import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, Download, ShieldAlert, FileText, Package } from "lucide-react";

const fmtDate = (v) => {
  if (!v) return "—";
  const head = String(v).split("T")[0];
  const m = head.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : head;
};
const fmtBytes = (n) => {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / 1024 / 1024).toFixed(1)} MB`;
};
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth landing page for a lender-package share link.
 * Lender opens `/share/lender-package/{token}` and sees a polished page
 * (no nav, no app chrome) with the inclusion checklist + Download ZIP.
 * Audit views are recorded server-side so the borrower can see who opened
 * what package via the Activity Bell.
 */
export default function SharedLenderPackage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | password | viewing | error
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API_BASE}/api/lender-package/share/${token}`);
        setMeta(m.data);
        if (m.data.needs_password) {
          setPhase("password");
        } else {
          await loadView("");
        }
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 410) setErrorText(detail || "This link is no longer available.");
        else if (status === 404) setErrorText("This share link doesn't exist.");
        else setErrorText("Could not load this share link.");
        setPhase("error");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadView = async (pw) => {
    setSubmitting(true);
    setErrorText("");
    try {
      const r = await axios.post(
        `${API_BASE}/api/lender-package/share/${token}/view`,
        { password: pw },
      );
      setData(r.data);
      setPhase("viewing");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        setErrorText("Password required" + (pw ? " — that password didn't match." : "."));
        setPhase("password");
      } else if (status === 410) {
        setErrorText(detail || "This link is no longer available.");
        setPhase("error");
      } else {
        setErrorText("Could not load this lender package.");
        setPhase("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadZip = () => {
    const url = `${API_BASE}/api/lender-package/share/${token}/download${password ? `?pw=${encodeURIComponent(password)}` : ""}`;
    window.open(url, "_blank");
  };

  // ---------- Phase: loading ----------
  if (phase === "loading") {
    return (
      <FullScreenWrap>
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading shared package…
        </div>
      </FullScreenWrap>
    );
  }

  // ---------- Phase: error ----------
  if (phase === "error") {
    return (
      <FullScreenWrap>
        <div className="bg-white border border-rose-200 rounded-sm p-8 max-w-md text-center" data-testid="lpshare-error">
          <ShieldAlert className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>
            Link unavailable
          </h1>
          <p className="text-sm text-slate-600">{errorText}</p>
          <p className="text-xs text-slate-400 mt-4 italic">Please reach out to the borrower for a new link.</p>
        </div>
      </FullScreenWrap>
    );
  }

  // ---------- Phase: password ----------
  if (phase === "password") {
    return (
      <FullScreenWrap>
        <form
          onSubmit={(e) => { e.preventDefault(); loadView(password); }}
          className="bg-white border border-slate-200 rounded-sm p-8 max-w-md w-full"
          data-testid="lpshare-password-form"
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-slate-900 text-white mx-auto mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-1 text-center" style={{ fontFamily: "Outfit" }}>
            Password protected
          </h1>
          {meta?.recipient_label && (
            <p className="text-sm text-slate-600 text-center mb-6">
              Hi <strong>{meta.recipient_label}</strong> — enter the password the borrower shared with you.
            </p>
          )}
          <input
            type="password" autoFocus required
            className="input-clean w-full mb-3"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            data-testid="lpshare-password-input"
          />
          {errorText && (
            <div className="text-xs text-rose-600 mb-3" data-testid="lpshare-password-error">{errorText}</div>
          )}
          <button
            type="submit" disabled={submitting || !password}
            className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold w-full py-2 rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="lpshare-password-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open package"}
          </button>
        </form>
      </FullScreenWrap>
    );
  }

  // ---------- Phase: viewing ----------
  const sharedBy = data?.shared_by || {};
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {sharedBy.logo_data_uri ? (
              <img
                src={sharedBy.logo_data_uri}
                alt={sharedBy.company || "Company logo"}
                className="w-10 h-10 object-contain rounded-sm border border-slate-200 bg-white p-0.5"
                data-testid="lpshare-logo"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                <Package className="w-5 h-5" />
              </div>
            )}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Lender package</div>
              <div className="text-sm font-bold text-slate-900">
                {sharedBy.company || sharedBy.name || "Borrower"}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="overline text-slate-500">Generated</div>
            <div className="text-sm font-mono text-slate-700">{fmtDate(meta?.created_at)}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6" data-testid="lpshare-viewing">
        {/* Hero */}
        <section className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Underwriting package ready
          </h1>
          {data?.recipient_label ? (
            <p className="text-slate-600 mb-1">
              Prepared for <strong>{data.recipient_label}</strong>
            </p>
          ) : (
            <p className="text-slate-600 mb-1">Borrower-prepared underwriting package</p>
          )}
          <p className="text-xs text-slate-400 mb-6">
            {(data?.included_files || []).length} document{(data?.included_files || []).length === 1 ? "" : "s"}
            {data?.zip_size_bytes ? ` · ${fmtBytes(data.zip_size_bytes)}` : ""}
          </p>
          <button
            type="button"
            onClick={downloadZip}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-sm inline-flex items-center gap-2 transition-colors"
            data-testid="lpshare-download-btn"
          >
            <Download className="w-4 h-4" /> Download ZIP
          </button>
        </section>

        {/* Included files list */}
        <section className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="overline text-slate-700 mb-4 flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> Inside the package
          </div>
          <ul className="divide-y divide-slate-100" data-testid="lpshare-files">
            {(data?.included_files || []).map((name) => (
              <li key={name} className="flex items-center justify-between py-3">
                <span className="text-sm font-mono text-slate-700">{name}</span>
                <span className="text-xs text-slate-400">PDF</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Headline summary if available */}
        {data?.summary && Object.keys(data.summary).length > 0 && (
          <section className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="overline text-slate-700 mb-3">Headline figures</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {data.summary.pfs_net_worth !== undefined && (
                <SummaryLine label="PFS net worth" value={`$${Math.round(data.summary.pfs_net_worth).toLocaleString()}`} />
              )}
              {data.summary.rent_collection_rate !== undefined && (
                <SummaryLine
                  label="Rent collection (12 mo)"
                  value={`${data.summary.rent_collection_rate}% · ${data.summary.lease_count || 0} leases`}
                />
              )}
              {data.summary.binder_year && (
                <SummaryLine label="Most recent binder" value={String(data.summary.binder_year)} />
              )}
              {data.summary.comp_count && (
                <SummaryLine label="Comparable listings" value={`${data.summary.comp_count} comps`} />
              )}
            </dl>
          </section>
        )}

        <div className="text-center text-xs text-slate-400 italic mt-8">
          This package was shared with you as a frozen snapshot. The borrower certifies the
          information is accurate as of the generation date.
        </div>
      </main>
    </div>
  );
}

function FullScreenWrap({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      {children}
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-mono font-semibold text-slate-900 tabular-nums">{value}</dd>
    </div>
  );
}
